import { Dispatch } from 'redux';
import {
  GUIDED_TOURS_FETCH_ERROR,
  GUIDED_TOURS_FETCH_START,
  GUIDED_TOURS_FETCH_SUCCESS,
  GUIDED_TOURS_HYDRATE,
  GUIDED_TOURS_MARK_COMPLETED,
  GUIDED_TOURS_SYNC_FAILED,
  GUIDED_TOURS_SYNC_SUCCEEDED,
} from '@core/constants/actions';
import * as api from '@core/api/guidedTours/guidedTourPreferences';
import type { GuidedToursMap } from '@app/utils/guidedTourStorage';
import {
  mergeGuidedTours,
  readGuidedToursFromStorage,
} from '@app/utils/guidedTourStorage';
import type { PendingTourSync } from '@app/reducers/guidedTours';

const MAX_SYNC_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

type GetState = () => {
  user: { userId: string | null };
  guidedTours: {
    tours: GuidedToursMap;
    status: 'idle' | 'loading' | 'ready' | 'error';
    pendingSync: PendingTourSync[];
  };
};

const syncGuidedTourPreference = async (
  dispatch: Dispatch,
  tourId: string,
  variant: string,
  attempt = 1
): Promise<void> => {
  try {
    const response = await api.patchGuidedTourPreference({ tourId, variant });
    dispatch({
      type: GUIDED_TOURS_SYNC_SUCCEEDED,
      tourId,
      variant,
      tours: response.tours,
    });
  } catch (error) {
    if (attempt < MAX_SYNC_ATTEMPTS) {
      await sleep(RETRY_DELAY_MS);
      await syncGuidedTourPreference(dispatch, tourId, variant, attempt + 1);
      return;
    }
    dispatch({
      type: GUIDED_TOURS_SYNC_FAILED,
      tourId,
      variant,
    });
  }
};

const replayPendingSync = (
  dispatch: Dispatch,
  pendingSync: PendingTourSync[]
) => {
  pendingSync
    .filter((entry) => entry.attempts < MAX_SYNC_ATTEMPTS)
    .forEach(({ tourId, variant }) => {
      syncGuidedTourPreference(dispatch, tourId, variant);
    });
};

export const hydrateGuidedTours = (userId: string) => ({
  type: GUIDED_TOURS_HYDRATE,
  userId,
  tours: readGuidedToursFromStorage(userId),
});

let fetchGuidedToursInFlight: Promise<void> | null = null;

export const fetchGuidedTourPreferences =
  () => async (dispatch: Dispatch, getState: GetState) => {
    const { userId } = getState().user;
    if (!userId) {
      return;
    }

    const { status } = getState().guidedTours;
    if (status === 'ready' || fetchGuidedToursInFlight) {
      return fetchGuidedToursInFlight || undefined;
    }

    fetchGuidedToursInFlight = (async () => {
      dispatch(hydrateGuidedTours(userId));
      dispatch({ type: GUIDED_TOURS_FETCH_START });

      try {
        const response = await api.fetchGuidedTourPreferences();
        const { tours: localTours } = getState().guidedTours;
        const mergedTours = mergeGuidedTours(localTours, response.tours || {});

        dispatch({
          type: GUIDED_TOURS_FETCH_SUCCESS,
          tours: mergedTours,
        });

        replayPendingSync(dispatch, getState().guidedTours.pendingSync);
      } catch (error) {
        dispatch({ type: GUIDED_TOURS_FETCH_ERROR });
      } finally {
        fetchGuidedToursInFlight = null;
      }
    })();

    return fetchGuidedToursInFlight;
  };

export const markGuidedTourCompleted =
  (tourId: string, variant: string) => (dispatch: Dispatch) => {
    dispatch({
      type: GUIDED_TOURS_MARK_COMPLETED,
      tourId,
      variant,
    });
    syncGuidedTourPreference(dispatch, tourId, variant);
  };
